/**
 * Aufbereitung der Nachrichtenliste zu Zeilen: Datumstrenner, „Neu“-Linie, Gruppierung aufeinanderfolgender
 * Nachrichten desselben Autors innerhalb eines Zeitfensters. Reine Funktionen (getestet in grouping.test.ts).
 */
import { compareSnowflakes, type MessageDto, type MessageType } from '@lagerfeuer/shared';
import type { PendingMessage } from '../../store/types';
import { formatDayDivider, sameDay } from './format';

/** Nachrichten desselben Autors innerhalb dieses Fensters werden zusammengefasst. */
export const GROUP_WINDOW_MS = 7 * 60_000;

const SYSTEM_TYPES: ReadonlySet<MessageType> = new Set<MessageType>([
  'SYSTEM',
  'THREAD_CREATED',
  'MEMBER_JOIN',
  'PIN_ADDED',
  'CHANNEL_NAME_CHANGE',
  'RECIPIENT_ADD',
  'RECIPIENT_REMOVE',
  'CALL',
]);

export function isSystemMessage(m: Pick<MessageDto, 'type'>): boolean {
  return SYSTEM_TYPES.has(m.type);
}

/** Schlüssel für „gleicher Absender“ (Webhooks mit unterschiedlichem Namen gelten als verschieden). */
export function authorKey(m: Pick<MessageDto, 'author' | 'webhookId' | 'overrideName'>): string {
  if (m.webhookId) return `w:${m.webhookId}:${m.overrideName ?? ''}`;
  return `u:${m.author?.id ?? '?'}`;
}

interface Groupable {
  type: MessageType;
  author: MessageDto['author'];
  webhookId: string | null;
  overrideName: string | null;
  createdAt: string;
  replyTo: MessageDto['replyTo'];
}

export function canGroup(prev: Groupable, next: Groupable): boolean {
  if (isSystemMessage(prev) || isSystemMessage(next)) return false;
  if (next.replyTo) return false;
  if (authorKey(prev) !== authorKey(next)) return false;
  const a = new Date(prev.createdAt);
  const b = new Date(next.createdAt);
  const diff = b.getTime() - a.getTime();
  return diff >= 0 && diff < GROUP_WINDOW_MS && sameDay(a, b);
}

export type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'new'; key: string }
  | { kind: 'message'; key: string; message: MessageDto; grouped: boolean }
  | { kind: 'pending'; key: string; pending: PendingMessage; grouped: boolean };

export interface BuildRowsOptions {
  /** Zuletzt gelesene Nachricht beim Öffnen des Kanals – davor steht die „Neu“-Linie. */
  newAfterId?: string | null;
  meId?: string | null;
  /** Eigene, noch nicht bestätigte Nachrichten (nur wenn der Ausschnitt am Ende liegt). */
  pending?: PendingMessage[];
  /** Ausgeblendete Tombstones (gelöscht ohne Verweise) nicht anzeigen. */
  hideDeleted?: boolean;
}

export function pendingAsGroupable(p: PendingMessage, me: MessageDto['author']): Groupable {
  return { type: p.replyToId ? 'REPLY' : 'DEFAULT', author: me, webhookId: null, overrideName: null, createdAt: p.createdAt, replyTo: p.replyToId ? { messageId: p.replyToId, channelId: p.channelId } : null };
}

export function buildRows(items: MessageDto[], opts: BuildRowsOptions = {}, me: MessageDto['author'] = null): Row[] {
  const rows: Row[] = [];
  let prev: Groupable | null = null;
  let newPlaced = false;
  for (const m of items) {
    if (opts.hideDeleted && m.deleted && !m.thread) continue;
    const created = new Date(m.createdAt);
    if (!prev || !sameDay(new Date(prev.createdAt), created)) {
      rows.push({ kind: 'day', key: `day-${m.id}`, label: formatDayDivider(m.createdAt) });
      prev = null;
    }
    if (!newPlaced && opts.newAfterId && compareSnowflakes(m.id, opts.newAfterId) > 0 && m.author?.id !== opts.meId) {
      rows.push({ kind: 'new', key: 'new' });
      newPlaced = true;
      prev = null;
    }
    rows.push({ kind: 'message', key: m.id, message: m, grouped: !!prev && !m.deleted && canGroup(prev, m) });
    prev = m;
  }
  for (const p of opts.pending ?? []) {
    const g = pendingAsGroupable(p, me);
    if (prev && !sameDay(new Date(prev.createdAt), new Date(p.createdAt))) {
      rows.push({ kind: 'day', key: `day-${p.nonce}`, label: formatDayDivider(p.createdAt) });
      prev = null;
    }
    rows.push({ kind: 'pending', key: `pending-${p.nonce}`, pending: p, grouped: !!prev && canGroup(prev, g) });
    prev = g;
  }
  return rows;
}
